import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React from 'react';

type CountryCode = keyof typeof Cornerstone.Country;

export type Country = {
  value: CountryCode;
  label?: string;
};

export const useCountries = () => {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<Country[]>([]);
  const { i18n } = useTranslation();

  // Just faking an API request for now
  React.useEffect(() => {
    const getCountries = async () => {
      const regionNames = new Intl.DisplayNames([i18n.language], {
        type: 'region',
      });
      const countries = Object.values(Cornerstone.Country).map((code) => ({
        value: code,
        label: regionNames.of(code),
      }));

      setData(countries);
      setLoading(false);
    };

    getCountries();
  }, [i18n, setData, setLoading]);

  return { countries: data, loading };
};

export type Region = {
  value: string;
  label?: string;
};

const REGIONS_BY_COUNTRIES: Record<
  string,
  { name: string; divisions: Record<string, string>; label: string }
> = {
  US: {
    name: 'United States',
    label: 'State',
    divisions: {
      'US-AL': 'Alabama',
      'US-AK': 'Alaska',
      'US-AZ': 'Arizona',
      'US-AR': 'Arkansas',
      'US-CA': 'California',
      'US-CO': 'Colorado',
      'US-CT': 'Connecticut',
      'US-DE': 'Delaware',
      'US-FL': 'Florida',
      'US-GA': 'Georgia',
      'US-HI': 'Hawaii',
      'US-ID': 'Idaho',
      'US-IL': 'Illinois',
      'US-IN': 'Indiana',
      'US-IA': 'Iowa',
      'US-KS': 'Kansas',
      'US-KY': 'Kentucky',
      'US-LA': 'Louisiana',
      'US-ME': 'Maine',
      'US-MD': 'Maryland',
      'US-MA': 'Massachusetts',
      'US-MI': 'Michigan',
      'US-MN': 'Minnesota',
      'US-MS': 'Mississippi',
      'US-MO': 'Missouri',
      'US-MT': 'Montana',
      'US-NE': 'Nebraska',
      'US-NV': 'Nevada',
      'US-NH': 'New Hampshire',
      'US-NJ': 'New Jersey',
      'US-NM': 'New Mexico',
      'US-NY': 'New York',
      'US-NC': 'North Carolina',
      'US-ND': 'North Dakota',
      'US-OH': 'Ohio',
      'US-OK': 'Oklahoma',
      'US-OR': 'Oregon',
      'US-PA': 'Pennsylvania',
      'US-RI': 'Rhode Island',
      'US-SC': 'South Carolina',
      'US-SD': 'South Dakota',
      'US-TN': 'Tennessee',
      'US-TX': 'Texas',
      'US-UT': 'Utah',
      'US-VT': 'Vermont',
      'US-VA': 'Virginia',
      'US-WA': 'Washington',
      'US-WV': 'West Virginia',
      'US-WI': 'Wisconsin',
      'US-WY': 'Wyoming',
      // Commenting out non-states for now
      // 'US-DC': 'District of Columbia',
      // 'US-AS': 'American Samoa',
      // 'US-GU': 'Guam',
      // 'US-MP': 'Northern Mariana Islands',
      // 'US-PR': 'Puerto Rico',
      // 'US-UM': 'United States Minor Outlying Islands',
      // 'US-VI': 'Virgin Islands, U.S.',
    },
  },
};

export const useRegions = (country?: string) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'route.user.addresses',
  });
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<Region[]>([]);
  const [label, setLabel] = React.useState('');

  React.useEffect(() => {
    const getRegions = async () => {
      setLoading(true);
      setData([]);
      setLabel('');

      if (!country || !REGIONS_BY_COUNTRIES[country]) {
        setLoading(false);
        setError(t('input.region.invalid', { country })!);
        return;
      }

      const { divisions, label } = REGIONS_BY_COUNTRIES[country] || {};
      const regions = Object.keys(divisions).map((region) => ({
        value: region.split(`${country}-`)[1],
        label: divisions[region],
      }));

      setData(regions);
      setLabel(label);
      setError(null);
      setLoading(false);
    };

    if (country) {
      getRegions();
    }
  }, [country, t]);

  return { label, regions: data, loading, error };
};
